import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs/operators';
import { TemplateStoreService } from '@modules/template/student/store/services/template-store.service';
import { ClassroomCourseFilter } from '@modules/course/classroom/models';
import { Location } from '@modules/location/models/location';
import { addDays, format, parseISO } from 'date-fns';
import { TRANSLATION_SCOPE } from '../../i18n/scope';
import { LocationApiService } from '@modules/location/store/services/location-api.service';
import { FormControl } from '@angular/forms';
import { FilterSearchComponent } from '@shared/components/filter-search/filter-search.component';
import { isFalsyValue } from '@shared/utils/object';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FilterBottomSheetComponent } from '@core/components/filter-bottom-sheet/filter-bottom-sheet.component';
import {
  BottomSheetFilter,
  CheckList,
  CheckOption, DateFilter,
  SelectedCheckOptions
} from '@core/models/bottom-sheet-filter';
import { TranslocoService } from '@ngneat/transloco';
import { isEqual } from 'lodash';
import { CourseFilters } from '@modules/course/all-course/models/course-filters';

@Component({
  selector: 'app-template-filter',
  templateUrl: './template-filter.component.html',
  styleUrls: ['./template-filter.component.scss']
})
export class TemplateFilterComponent implements OnInit, OnDestroy {
  @ViewChild(FilterSearchComponent) filterSearchComponent: FilterSearchComponent;

  @Input() public templateId: string;
  @Input() public HideDate = false;
  @Input() public HideCity = false;

  activeFilterCount: number = 0;
  filter: ClassroomCourseFilter;
  isSearchResetting: boolean = false;
  selectedRegions: string[] = [];
  searchFormControl: FormControl = new FormControl('');

  public translationScope = TRANSLATION_SCOPE;

  regions: Location[] = [];
  private destroy$: Subject<any> = new Subject();
  isFiltersLoaded: boolean = true;

  isBottomSheetFilterUpdated: boolean = false;
  bottomSheetFilters: CheckList[] = [];
  bottomSheetFilterOptions: CheckOption[];

  constructor(
    private templateStore: TemplateStoreService,
    private locationApiService: LocationApiService,
    private translationService: TranslocoService,
    private bottomSheet: MatBottomSheet
  ) {}

  ngOnInit() {
    this.trackSearchFormControlValue();
    this.getRegions();
    this.getClassroomFilter();
  }

  saveDate(filter: ClassroomCourseFilter, dateRange: any): boolean {
    if (dateRange.from !== null) {
      filter = this.convert(filter);
      filter.fromDate = dateRange.from;
      filter.toDate = dateRange.to;
      filter.dateFilter = '';
      this.templateStore.addClassroomFilter(filter);

      return true;
    } else {
      return false;
    }
  }

  clearDate(filter: ClassroomCourseFilter, dateRange: any): void {
    filter = this.convert(filter);
    filter.fromDate = dateRange.from;
    filter.toDate = dateRange.to;
    filter.dateFilter = format(addDays(new Date(), 1), 'yyyy-MM-dd');
    this.templateStore.addClassroomFilter(filter);
  }

  getDateFilterLabel(from: string, to: string): string {
    if (!from && !to) {
      return 'Dato';
    }

    if (to === null) {
      return format(parseISO(from), 'dd.MM.yyyy');
    } else {
      return `${format(parseISO(from), 'dd.MM.yyyy')} - ${format(parseISO(to), 'dd.MM.yyyy')}`;
    }
  }

  ngOnDestroy(): void {
    this.templateStore.resetClassroomFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateSearchFilter(searchValue: string = '') {
    this.searchFormControl.patchValue(searchValue);
  }

  updateRegionsFilter() {
    if (this.filter.locations === this.selectedRegions || (isFalsyValue(this.filter.locations) && isFalsyValue(this.selectedRegions)))
      return;

    this.filter = { ...this.filter, locations: this.selectedRegions };
    this.filter.locations = this.selectedRegions;
    if (!this.filter.fromDate || !this.filter.toDate) {
      this.filter.dateFilter = format(addDays(new Date(), 1), 'yyyy-MM-dd');
    } else {
      this.filter.dateFilter = null;
    }

    this.templateStore.addClassroomFilter(this.filter);
  }

  clearRegionsFilter() {
    this.filter = { ...this.filter, locations: [] };
    if (!this.filter.fromDate || !this.filter.toDate) {
      this.filter.dateFilter = format(addDays(new Date(), 1), 'yyyy-MM-dd');
    } else {
      this.filter.dateFilter = null;
    }

    this.templateStore.addClassroomFilter(this.filter);
  }

  resetFiltersAndSearch() {
    if(
      isFalsyValue(this.filter.locations) &&
      isFalsyValue(this.filter.search) &&
      isFalsyValue(this.filter.fromDate)) {
      return;
    }

    this.isSearchResetting = true;
    this.filterSearchComponent.reset();
    this.searchFormControl.reset('');
    this.selectedRegions = [];
    this.templateStore.addClassroomFilter(new ClassroomCourseFilter());
  }

  private convert(filter: ClassroomCourseFilter): ClassroomCourseFilter {
    return JSON.parse(JSON.stringify(filter)) as ClassroomCourseFilter;
  }

  private getClassroomFilter(): void {
    this.templateStore.getClassroomFilter().pipe(
      tap((filter) => {
        if (this.templateId) {
          this.templateStore.fetchClassroomCourse(this.templateId, filter);
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe((filter) => {
      this.filter = filter;
      this.getActiveFilterCount();
    });
  }

  private getRegions(): void {
    this.locationApiService.getAllLocation('')
      .pipe(
        map((res) => res.items),
        take(1)
      ).subscribe((regions) => {
          this.regions = regions;
          this.setBottomSheetFilters();
      });
  }

  private trackSearchFormControlValue(): void {
    this.searchFormControl.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((searchText: string) => {
        if (!this.isSearchResetting) {
          this.filter = { ...this.filter, search: searchText };
          this.templateStore.addClassroomFilter(this.filter);
        } else {
          this.isSearchResetting = false;
        }
      });
  }

  private isSelectedFilterValueChange(currentValue: string[],previousValue: string[] = []): boolean {
    previousValue = previousValue.filter((value) => value !== '');
    if(isFalsyValue(currentValue)  && isFalsyValue(previousValue)) {
      return false
    }
    return (!isEqual(currentValue.sort(),previousValue.sort()));
  }

  openFilterBottomSheet() {
    this.getBottomSheetFilters();

    this.bottomSheet.open(FilterBottomSheetComponent,
      {
        data: {
          filters: this.bottomSheetFilters,
          search: {
            placeholder: this.translationService.translate(`${this.translationScope}.search`),
            value: this.filter.search ?? '' },
          dateRange: {
            from: this.filter.fromDate,
            to: this.filter.toDate
          }
        } as BottomSheetFilter,
        disableClose: true
      }).afterDismissed()
      .subscribe((filters: BottomSheetFilter) => {
        if (filters.resetAll) {
          this.resetFiltersAndSearch();
        } else {
          this.updateFiltersByBottomSheetSelection(this.getBottomSheetFilterSelectedOptions(filters.filters), filters.search.value, filters.dateRange);
          this.fetchBottomSheetFilteredCourses();
        }
      });
  }

  private setBottomSheetFilters(): void {
    this.bottomSheetFilterOptions = this.regions.map((region) => ({
      id: region.id,
      label: region.name,
      selected: false
    }));
    this.bottomSheetFilters.push(this.getBottomSheetFilterOptions(CourseFilters.REGION, this.translationService.translate(`${this.translationScope}.region`), this.bottomSheetFilterOptions));
  }

  private getBottomSheetFilters(): void {
    this.bottomSheetFilters.find(filter => filter.name === CourseFilters.REGION).options.forEach(option => {
      option.selected = this.selectedRegions.includes(option.id);
    });
  }

  private updateFiltersByBottomSheetSelection(selectedOptions: SelectedCheckOptions, searchText: string, dateRange: DateFilter): void {
    if (this.isSelectedFilterValueChange(this.selectedRegions, selectedOptions[CourseFilters.REGION])) {
      this.isBottomSheetFilterUpdated = true;
      this.filter = {
        ...this.filter,
        locations: selectedOptions[CourseFilters.REGION]
      }
      this.selectedRegions = selectedOptions[CourseFilters.REGION];
    }

    if (this.filter.search !== searchText) {
      this.isBottomSheetFilterUpdated = true;
      this.filter = { ...this.filter, search: searchText };
    }

    if(this.filter.fromDate !== dateRange.from && this.filter.toDate !== dateRange.to) {
      this.isBottomSheetFilterUpdated = true;
      this.filter = { ...this.filter, fromDate: dateRange.from, toDate: dateRange.to };
    }

    if(isFalsyValue(dateRange.from) && isFalsyValue(dateRange.to)) {
      this.filter = { ...this.filter, dateFilter: format(addDays(new Date(), 1), 'yyyy-MM-dd') };
    } else {
      this.filter = { ...this.filter, dateFilter: '' };
    }
  }

  private getBottomSheetFilterSelectedOptions(filters: CheckList[]): SelectedCheckOptions {
    return filters.reduce((selectedIds, filter) => {
      selectedIds[filter.name] = Object.values(filter.options)
        .filter(option => option.selected)
        .map(option => option.id);
      return selectedIds;
    }, {} as SelectedCheckOptions);
  }

  private getBottomSheetFilterOptions(name: string, label: string, options: CheckOption[]): CheckList {
    return { name, label, options } as CheckList;
  }

  private fetchBottomSheetFilteredCourses() {
    if (this.isBottomSheetFilterUpdated) {
      this.templateStore.addClassroomFilter(this.filter);
    }
    this.isBottomSheetFilterUpdated = false;
  }

  getActiveFilterCount(): void {
    this.activeFilterCount = 0;
    if (!isFalsyValue(this.filter.search)) {
      this.activeFilterCount++;
    }

    if (!isFalsyValue(this.filter.locations)) {
      this.activeFilterCount++;
    }

    if (!isFalsyValue(this.filter.fromDate)) {
      this.activeFilterCount++;
    }
  }
}
